// Builds an editable resume draft from the master profile.
//
// Copy, not reference: every entry is reconstructed field by field so editing a
// draft can never mutate the stored profile. Provenance (`state`) travels with
// each copied fact so unconfirmed material stays visible in the resume, and a
// draft never upgrades a `needs-review` fact to confirmed on its own.

import { nextId } from '../profile/model';
import type { MasterProfile } from '../profile/types';
import type {
  ResumeCertificationEntry,
  ResumeContact,
  ResumeDraft,
  ResumeEducationEntry,
  ResumeExperienceEntry,
  ResumeProjectEntry,
  ResumeSectionId,
  ResumeSkillEntry,
} from './types';
import { RESUME_SECTIONS } from './types';

export const EMPTY_DRAFT_NAME = 'Untitled resume';

function contactFromProfile(profile: MasterProfile): ResumeContact {
  return {
    fullName: { ...profile.contact.fullName },
    email: { ...profile.contact.email },
    phone: { ...profile.contact.phone },
    location: { ...profile.contact.location },
    links: profile.contact.links.map((link) => ({
      id: link.id,
      label: link.label,
      url: link.url,
      state: link.state,
    })),
  };
}

function experienceFrom(entry: MasterProfile['experience'][number]): ResumeExperienceEntry {
  return {
    id: entry.id,
    state: entry.state,
    evidence: entry.evidence,
    edited: false,
    organization: entry.organization,
    role: entry.role,
    location: entry.location,
    periodRaw: entry.period.raw,
    responsibilities: [...entry.responsibilities],
    achievements: [...entry.achievements],
  };
}

function educationFrom(entry: MasterProfile['education'][number]): ResumeEducationEntry {
  return {
    id: entry.id,
    state: entry.state,
    evidence: entry.evidence,
    edited: false,
    institution: entry.institution,
    credential: entry.credential,
    area: entry.area,
    periodRaw: entry.period.raw,
  };
}

function skillFrom(entry: MasterProfile['skills'][number]): ResumeSkillEntry {
  return { id: entry.id, state: entry.state, evidence: entry.evidence, edited: false, name: entry.name };
}

function certificationFrom(
  entry: MasterProfile['certifications'][number],
): ResumeCertificationEntry {
  return {
    id: entry.id,
    state: entry.state,
    evidence: entry.evidence,
    edited: false,
    name: entry.name,
    issuer: entry.issuer,
    date: entry.date,
  };
}

function projectFrom(entry: MasterProfile['projects'][number]): ResumeProjectEntry {
  return {
    id: entry.id,
    state: entry.state,
    evidence: entry.evidence,
    edited: false,
    name: entry.name,
    url: entry.url,
    description: entry.description,
    periodRaw: entry.period.raw,
  };
}

/** Sections that have at least one entry, in the default order. */
export function populatedSections(draft: ResumeDraft): ResumeSectionId[] {
  return RESUME_SECTIONS.filter((section) => sectionHasContent(draft, section));
}

export function sectionHasContent(draft: ResumeDraft, section: ResumeSectionId): boolean {
  switch (section) {
    case 'summary':
      return draft.summary.value.trim().length > 0;
    case 'experience':
      return draft.experience.length > 0;
    case 'education':
      return draft.education.length > 0;
    case 'skills':
      return draft.skills.length > 0;
    case 'certifications':
      return draft.certifications.length > 0;
    case 'projects':
      return draft.projects.length > 0;
  }
}

/**
 * Copies the profile into a fresh draft. Every section is included in the
 * default order; sections without content are hidden by the renderer, not
 * dropped, so filling the profile later does not require a new draft.
 */
export function draftFromProfile(
  profile: MasterProfile,
  name: string = EMPTY_DRAFT_NAME,
  now: string = new Date().toISOString(),
): ResumeDraft {
  return {
    id: nextId('draft'),
    name,
    targetRole: profile.experience[0]?.role ?? '',
    contact: contactFromProfile(profile),
    summary: { ...profile.summary },
    sectionOrder: [...RESUME_SECTIONS],
    experience: profile.experience.map(experienceFrom),
    education: profile.education.map(educationFrom),
    skills: profile.skills.map(skillFrom),
    certifications: profile.certifications.map(certificationFrom),
    projects: profile.projects.map(projectFrom),
    template: 'classic',
    updatedAt: now,
  };
}

/** True when the draft holds no resume-worthy content at all. */
export function isEmptyDraft(draft: ResumeDraft): boolean {
  return (
    draft.summary.value.trim().length === 0 &&
    draft.experience.length === 0 &&
    draft.education.length === 0 &&
    draft.skills.length === 0 &&
    draft.certifications.length === 0 &&
    draft.projects.length === 0
  );
}
